import { useEffect, useState, type ReactNode } from "react";
import { cn } from "../cn";

type Common = { className?: string };

/**
 * `copyable` narrows `children` to a string, and that is the point of the union
 * rather than a `value` prop beside it. A copy button whose payload is passed
 * separately is a copy button that can silently put something else on the
 * clipboard than the thing on screen, and nobody would ever see the difference
 * until a studio pasted the wrong URL into a vendor's dashboard.
 */
export type SnippetProps = Common &
  (
    | {
        copyable: true;
        children: string;
        /** The copy button's accessible name. Starts with "Copy", so the visible text stays in it. */
        copyLabel?: string;
        /** The button's visible text while idle. Defaults to "Copy". */
        copyText?: string;
        /** On a phone, put the button under the code instead of over its corner: full width for `true`, at its own width on the right for `"end"`. */
        stackOnPhone?: boolean | "end";
        /** Put the button in its own column beside the code, so a long one-line value scrolls inside its box and never runs under it. */
        beside?: boolean;
      }
    | { copyable?: false; children: ReactNode }
  );

/**
 * A block of code or configuration, shown to be read and copied.
 *
 * `<pre>` rather than a styled `<div>`, because the whitespace in a curl command
 * or a YAML document is the content. It scrolls inside its own box: a long line
 * in a snippet must never be the reason a page scrolls sideways.
 */
export function Snippet(props: SnippetProps) {
  if (props.copyable)
    return (
      <CopyableSnippet
        className={props.className}
        text={props.children}
        label={props.copyLabel}
        idleText={props.copyText ?? "Copy"}
        stack={props.stackOnPhone ?? false}
        beside={props.beside ?? false}
      />
    );
  return <Pre className={props.className}>{props.children}</Pre>;
}

function Pre({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <pre
      className={cn(
        "overflow-x-auto rounded-md bg-sunk p-4 font-mono text-ui leading-relaxed text-ink",
        className,
      )}
    >
      {children}
    </pre>
  );
}

/**
 * The same block with a button that puts it on the clipboard.
 *
 * A failed write says so instead of pretending. The clipboard API refuses in an
 * insecure context and in a browser whose permission was denied, and a button
 * that flips to "Copied" either way teaches somebody to paste a stale buffer
 * into the one field where being wrong is invisible — a vendor's own dashboard,
 * which is not somewhere we can go and look.
 */
function CopyableSnippet({
  className,
  text,
  label,
  idleText,
  stack,
  beside,
}: {
  className?: string;
  text: string;
  label: string | undefined;
  idleText: string;
  stack: boolean | "end";
  beside: boolean;
}) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (state === "idle") return;
    const timer = window.setTimeout(() => setState("idle"), 2400);
    return () => window.clearTimeout(timer);
  }, [state]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setState("copied");
    } catch {
      setState("failed");
    }
  }

  return (
    <div
      className={
        beside
          ? "flex items-center gap-2"
          : stack
            ? "flex flex-col gap-2 sm:relative sm:block"
            : "relative"
      }
    >
      <Pre
        className={cn(
          beside ? "min-w-0 flex-1 whitespace-pre" : stack ? "sm:pr-24" : "pr-24",
          className,
        )}
      >
        {text}
      </Pre>
      <button
        type="button"
        aria-label={state === "idle" ? label : undefined}
        onClick={() => void copy()}
        className={cn(
          beside
            ? "shrink-0"
            : stack === "end"
              ? "self-end sm:absolute sm:top-2 sm:right-2"
              : stack
                ? "w-full sm:absolute sm:top-2 sm:right-2 sm:w-auto"
                : "absolute top-2 right-2",
          "rounded-[var(--radius-control)] px-2 py-1",
          "border border-rule bg-surface-2 font-sans text-meta font-medium text-ink-2",
          "hover:bg-sunk hover:text-ink",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        )}
      >
        {state === "copied" ? "Copied" : state === "failed" ? "Select it instead" : idleText}
      </button>
      <span role="status" className="sr-only">
        {state === "copied"
          ? "Copied"
          : state === "failed"
            ? "Copy failed. Select the text instead."
            : ""}
      </span>
    </div>
  );
}
